import type { BreadcrumbProps } from '@blueprintjs/core'
import { HTMLSelect } from '../../components/ui/html-select'
import { subDays } from 'date-fns'
import React, { type ReactNode, useEffect, useMemo, useState } from 'react'
import { Layout } from '../../components/Layout'
import { useTableHooksFromStore } from '../../packages/blueprint-components/src/useTableHooks'
import { axiosGet } from '../../util/api'
import { defaultPerfColumns } from '../../components/table2/ad_grid_columns'
import { type AgGridColumnDef, AgGridTable, DummyHeader } from '../../components/table2/AgGridTable'
import { addExtraCols } from '../../components/campaigns/CampaignsTable'
import { TableStore } from '../../components/table2/table_store'
import { userStore } from '../../stores/user_store'
import { useSavedFiltersStore } from '../../hooks/useSavedFiltersStore'
import { observer } from 'mobx-react-lite'

const nGramTitle = (size: 1 | 2 | 3) => {
    if (size == 1) {
        return 'Monogram'
    } else if (size == 2) {
        return 'Bigram'
    } else {
        return 'Trigram'
    }
}

const agGridComponent = (component: unknown): ReactNode => component as ReactNode

export const NGrams = observer(() => {
    const [size, setSize] = useState<1 | 2 | 3>(1)
    const savedFiltersStore = useSavedFiltersStore()

    const [store] = useState<TableStore<NGramItem>>(new TableStore(userStore, 'ngrams'))

    const load = (params: any) => {
        if (!userStore.profileId) {
            return Promise.resolve()
        }

        // Hacky way of preventing the first load before the date ranges are applied
        if (!params.from) {
            params.from = subDays(new Date(), 30).toISOString()
        }
        if (!params.to) {
            params.to = new Date().toISOString()
        }

        console.log('params is', params)
        return axiosGet('api/v3/ngram', { ...params, size }, true)
    }
    const tableHooks2 = useTableHooksFromStore(load, {}, false, store)

    useEffect(() => {
        tableHooks2.reload()
    }, [size])

    const breadcrumbs: BreadcrumbProps[] = [{ text: 'Automate' }]
    const cols = useMemo(
        () => newColumns<NGramItem>(store.userStore.isKdp, size),
        [store.userStore.isKdp, size],
    )

    return (
        <Layout breadcrumbs={breadcrumbs} primary="N-Grams">
            <AgGridTable
                otherActionsLeft={
                    <HTMLSelect
                        options={[
                            { label: 'Monogram', value: 1 },
                            { label: 'Bigram', value: 2 },
                            { label: 'Trigram', value: 3 },
                        ]}
                        value={size}
                        onChange={(e) => setSize(e.target.value as any)}
                    />
                }
                name={'N-Grams'}
                columns={cols}
                tableHooks={tableHooks2}
                store={store}
                showHeader={false}
                savedFiltersStore={savedFiltersStore}
                search
                searchPlaceholder="Search"
                columnSelection
            />
        </Layout>
    )
})

const newColumns = <T,>(isKdp: boolean, size: 1 | 2 | 3): AgGridColumnDef<T>[] => {
    const cols: AgGridColumnDef<T>[] = [
        {
            field: 'search_term',
            headerName: nGramTitle(size),
            headerComponent: agGridComponent(DummyHeader),
        },
        ...defaultPerfColumns,
    ]

    addExtraCols(cols, isKdp)

    return cols.filter((c) => c.field !== 'target_acos' && c.field !== 'ideal_cpc')
}

interface NGramItem {
    search_term: string
    impressions: number
    clicks: number
    spend: number
    orders: number
    acos: number
    sales: number
}
